import { useState, useEffect } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Sparkles, ClipboardCheck, Lightbulb, Upload } from "lucide-react";

import { AuditDialog } from "@/features/library/AuditDialog";
import { SuggestDialog } from "@/features/library/SuggestDialog";
import { ImportDialog } from "@/features/library/ImportDialog";

/**
 * LibraryAssistant (P1.2) — single right-rail surface that unifies all
 * AI-powered library tools.
 *
 * Three modes:
 *   • Detect   — review existing blocks for issues (was AuditDialog)
 *   • Discover — suggest new blocks to fill gaps (was SuggestDialog)
 *   • Ingest   — import / parse external prompts (was ImportDialog)
 *
 * The mode-views are the SAME components as before; they accept an
 * `embedded` prop that strips their outer `<Dialog>` wrapper so they render
 * inline inside the Sheet.
 */
const MODES = [
  {
    id: "detect",
    label: "Detect",
    icon: ClipboardCheck,
    blurb: "Review your library for outdated, weak, or duplicate blocks.",
  },
  {
    id: "discover",
    label: "Discover",
    icon: Lightbulb,
    blurb: "Suggest new blocks to fill gaps in your library coverage.",
  },
  {
    id: "ingest",
    label: "Ingest",
    icon: Upload,
    blurb: "Paste external prompts; we parse them into structured blocks.",
  },
];

export function LibraryAssistant({
  blocksCount = 0,
  scope = null,
  initialMode = "",
  initialCategory = "",
  initialPanel = null, // "detect" | "discover" | "ingest" — opens the panel directly to that mode
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  onSuccess,
  trigger = null,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [activeMode, setActiveMode] = useState(initialPanel || "detect");

  const open = controlledOpen ?? internalOpen;
  const setOpen = setControlledOpen ?? setInternalOpen;

  // When the parent re-opens us with a specific panel intent, honor it.
  useEffect(() => {
    if (open && initialPanel) setActiveMode(initialPanel);
  }, [open, initialPanel]);

  const handleSuccess = () => {
    onSuccess?.();
    setOpen(false);
  };

  const ActiveIcon = MODES.find((m) => m.id === activeMode)?.icon || Sparkles;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger ?? (
          <Button
            data-testid="library-assistant-trigger"
            className="bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white rounded-none h-9 px-4 font-medium gap-1.5"
          >
            <Sparkles className="w-4 h-4" />
            AI Assistant
          </Button>
        )}
      </SheetTrigger>
      <SheetContent
        side="right"
        data-testid="library-assistant-sheet"
        className="w-full sm:max-w-2xl rounded-none border-l border-[var(--pf-border)] p-0 flex flex-col"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[var(--pf-border)] shrink-0">
          <SheetTitle className="font-heading text-xl font-bold flex items-center gap-2">
            <ActiveIcon className="w-5 h-5 text-[var(--pf-accent)]" />
            Library Assistant
          </SheetTitle>
          <p className="text-xs text-[var(--pf-text-secondary)] -mt-0.5">
            One AI surface for keeping your library curated and growing.
          </p>
        </SheetHeader>

        {/* Mode tab strip */}
        <div className="flex border-b border-[var(--pf-border)] shrink-0" role="tablist">
          {MODES.map((m) => {
            const Icon = m.icon;
            const isActive = activeMode === m.id;
            return (
              <button
                key={m.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveMode(m.id)}
                data-testid={`assistant-tab-${m.id}`}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium uppercase tracking-[0.15em] transition-colors ${
                  isActive
                    ? "text-[var(--pf-accent)] border-b-2 border-[var(--pf-accent)] -mb-px"
                    : "text-[var(--pf-text-muted)] hover:text-[var(--pf-text)]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Active mode body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-w-0">
          <p className="text-[11px] text-[var(--pf-text-muted)] mb-3 font-mono uppercase tracking-[0.15em]">
            {MODES.find((m) => m.id === activeMode)?.blurb}
          </p>

          {activeMode === "detect" && (
            <AuditDialog
              embedded
              open
              onOpenChange={() => {}}
              onSuccess={handleSuccess}
              blocksCount={blocksCount}
              scope={scope}
            />
          )}
          {activeMode === "discover" && (
            <SuggestDialog
              embedded
              open
              onOpenChange={() => {}}
              onSuccess={handleSuccess}
              initialMode={initialMode}
              initialCategory={initialCategory}
            />
          )}
          {activeMode === "ingest" && (
            <ImportDialog
              embedded
              open
              onOpenChange={() => {}}
              onSuccess={handleSuccess}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
